import type { GridPinnedColumnFields } from "@mui/x-data-grid-premium";
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { FieldConfiguration } from "../../../../../../api/types/fieldConfigurationTypes.ts";
import { FieldConfigurationType } from "../../../../../../api/types/fieldConfigurationTypes.ts";
import type { GroupingField, ReportField, SortField } from "../../../../../../api/types/svTypes.ts";
import { AreaItemType, Ordering, SortType } from "../../../../../../api/types/svTypes.ts";
import { isReferenceDateField } from "../../components/builder/common/utilities/isReferenceDateField.ts";
import { getGroupMetaNames } from "../../components/builder/common/utilities/sortFieldStateHelper.ts";
import {
  rebuildSortingFields,
  type SortingFieldModel,
} from "../../components/builder/tabular/preview/rebuildSortingFields.ts";
import { handlePinnedColumnsChange } from "../../components/viewer/tabular/utils/pinnedColumnsHelper.ts";
import { isAggregatedType, isCustomColumn, isDimensionBased, isMeasure } from "../../utilities/fieldsHelper.ts";
import {
  selectSorts,
  selectTabularFields,
  selectTabularFreezedColumns,
  selectTabularGrouping,
  selectTabularShowGroupedColumns,
} from "../selectors/fieldsStateSelectors.ts";
import { createTypedAsyncThunk } from "../utils/createTypedAsyncThunk.ts";
import { createTabularAction } from "../utils/storeUtils.ts";

const updateTabularFieldConfig = createTypedAsyncThunk<
  void,
  { field: ReportField; changes: Partial<FieldConfiguration> }
>(
  "tabularFields/updateFieldConfig",
  (payload: { field: ReportField; changes: Partial<FieldConfiguration> }, { dispatch, getState }) => {
    const { field, changes } = payload;
    dispatch(createTabularAction("updateFieldConfig", payload));
    if (!isDimensionBased(field)) {
      return;
    }
    const isAggregatedSwitchedOn = isAggregatedType(changes);
    const state = getState();
    const groupingField = state.fieldsState.tabularFieldsReducer.grouping.find((g) => g.id === field.config.guid);
    const hasToRemoveGroup = groupingField && isAggregatedSwitchedOn;
    if (hasToRemoveGroup) {
      dispatch(removeGroup(groupingField));
    }

    const isAggregationSwitchedOff =
      isAggregatedType(field.config) &&
      changes.type !== undefined &&
      changes.type !== FieldConfigurationType.Aggregation;
    if (isReferenceDateField(field.meta) && isAggregationSwitchedOff) {
      const measureGroups = state.fieldsState.tabularFieldsReducer.grouping.filter((g) => {
        const gf = state.fieldsState.tabularFieldsReducer.fields.find((f) => f.config.guid === g.id);
        return gf && isMeasure(gf);
      });
      measureGroups.forEach((g) => dispatch(removeGroup(g)));
    }

    const sortingField = state.fieldsState.tabularFieldsReducer.sorts.find((s) => s.config.id === field.config.guid);
    if (!sortingField) {
      return;
    }
    if (field.config?.customLabel !== changes.customLabel) {
      dispatch(
        createTabularAction("updateSortingConfig", {
          field: sortingField,
          changes: { caption: changes.customLabel },
        })
      );
    }
  }
);

const setGrouping = createAsyncThunk<void, GroupingField[]>(
  "tabularFields/setGrouping",
  (groups: GroupingField[], { dispatch }) => {
    dispatch(createTabularAction("setGrouping", groups));
  }
);

const updateGroup = createAsyncThunk<void, { group: GroupingField; changes: Partial<GroupingField> }>(
  "tabularFields/updateGroup",
  (payload: { group: GroupingField; changes: Partial<GroupingField> }, { dispatch }) => {
    dispatch(createTabularAction("updateGroup", payload));
  }
);

const addGroup = createTypedAsyncThunk<void, GroupingField>(
  "tabularFields/addGroup",
  (group: GroupingField, { dispatch, getState }) => {
    const state = getState();

    const field = state.fieldsState.tabularFieldsReducer.fields.find((f) => f.config.guid === group.id);
    if (field && field.meta) {
      const optimisticGroups = [...state.fieldsState.tabularFieldsReducer.grouping, group];
      const sortingField = state.fieldsState.tabularFieldsReducer.sorts.find((s) => s.config.id === field.config.guid);
      if (sortingField) {
        dispatch(createTabularAction("removeSorting", sortingField));
      }
      dispatch(
        addGroupSorting({
          field,
          groupMetaNames: getGroupMetaNames(optimisticGroups, state.fieldsState.tabularFieldsReducer.fields),
        })
      );
    }
    dispatch(createTabularAction("addGroup", group));
  }
);

const removeGroup = createTypedAsyncThunk<void, GroupingField>(
  "tabularFields/removeGroup",
  (group: GroupingField, { dispatch, getState }) => {
    const state = getState();
    const field = state.fieldsState.tabularFieldsReducer.fields.find((f) => f.config.guid === group.id);
    if (field && field.meta) {
      const sortingField = state.fieldsState.tabularFieldsReducer.sorts.find((s) => s.config.id === field.config.guid);
      if (sortingField) {
        dispatch(createTabularAction("removeSorting", sortingField));
      }
    }

    dispatch(createTabularAction("removeGroup", group));
  }
);

const moveGroup = createAsyncThunk<void, { field: GroupingField; newIndex: number }>(
  "tabularFields/moveGroup",
  (payload: { field: GroupingField; newIndex: number }, { dispatch }) => {
    dispatch(createTabularAction("moveGroup", payload));
  }
);

const addGroupSorting = createAsyncThunk<void, { field: ReportField; groupMetaNames: string[] }>(
  "tabularFields/addGroupSorting",
  (payload: { field: ReportField; groupMetaNames: string[] }, { dispatch }) => {
    const { field, groupMetaNames } = payload;
    const type = isDimensionBased(field)
      ? SortType.Dimension
      : isCustomColumn(field)
        ? SortType.Custom
        : isMeasure(field)
          ? SortType.Measure
          : SortType.Dimension;
    const sortField: SortField = {
      meta: field.meta,
      config: {
        name: field.meta.name,
        id: field.config.guid,
        ordering: Ordering.Ascending,
        caption: field.config.customLabel || field.meta.caption,
        isGroupField: true,
        type,
      },
      areaItemType: AreaItemType.SORTS,
    };
    dispatch(createTabularAction("addGroupSorting", { field: sortField, groupMetaNames }));
  }
);

const setFreezedColumns = createTypedAsyncThunk<void, GridPinnedColumnFields>(
  "tabularFields/setFreezedColumns",
  (freezedColumns: GridPinnedColumnFields, { dispatch, getState }) => {
    const state = getState();
    const freezedColumnIds = selectTabularFreezedColumns(state);
    const groupAreaValues = selectTabularGrouping(state);
    const showGroupedColumns = selectTabularShowGroupedColumns(state);

    const newFreezedColumns = handlePinnedColumnsChange(
      freezedColumns,
      freezedColumnIds,
      groupAreaValues,
      showGroupedColumns
    );
    dispatch(createTabularAction("updateSettings", { freezedColumns: newFreezedColumns }));
  }
);

const updateSorting = createTypedAsyncThunk<void, SortingFieldModel>(
  "fields/updateSorting",
  (newSorting, { dispatch, getState }) => {
    const state = getState();
    const fields = selectTabularFields(state);
    const grouping = selectTabularGrouping(state);
    const sorts = selectSorts(state);
    const sortFields = rebuildSortingFields(newSorting, fields, sorts);
    if (sortFields.length === 0) {
      return;
    }

    dispatch(
      createTabularAction("setSorting", {
        fields: sortFields,
        groupMetaNames: getGroupMetaNames(grouping, fields),
      })
    );
  }
);

export const tabularFieldsStateThunks = {
  updateTabularFieldConfig,
  setGrouping,
  updateGroup,
  addGroup,
  removeGroup,
  moveGroup,
  addGroupSorting,
  setFreezedColumns,
  updateSorting,
};
